/**
 * @class BaseSlider
 * @extends HTMLElement
 *
 * A reusable Swiper-based custom slider element.
 */
export class BaseSlider extends HTMLElement {
  #swiperInstance = null;
  #scrollController = null;

  #onInit = () => this.initSlider();
  #onUpdate = () => this.updateSlider();
  #onDestroy = () => this.destroySlider();

  connectedCallback() {
    this.addEventListener('slider:init', this.#onInit);
    this.addEventListener('slider:update', this.#onUpdate);
    this.addEventListener('slider:destroy', this.#onDestroy);

    this.initSlider();
    this.#stopPropagationForScrollableChildren();
  }

  disconnectedCallback() {
    this.removeEventListener('slider:init', this.#onInit);
    this.removeEventListener('slider:update', this.#onUpdate);
    this.removeEventListener('slider:destroy', this.#onDestroy);

    this.#scrollController?.abort();
    this.#scrollController = null;

    this.destroySlider();
  }

  get #defaultOptions() {
    return {
      slidesPerView: 'auto',
      spaceBetween: 0,
      navigation: false,
      pagination: false,
      scrollbar: false,
      grabCursor: true,
      on: {
        afterInit: () => {
          this.classList.add(this.modifiers.initialized);
          this.#updateSliderStateModifiers();
        },
        toEdge: () => this.#onSlideChange(),
        slideChange: () => this.#onSlideChange(),
      },
    };
  }

  get selectors() {
    return {
      wrapper: '.swiper-wrapper',
      slide: 'slider-slide',
      scrollableChildren: '.swiper-no-swiping',
    };
  }

  get modifiers() {
    return {
      initialized: 'initialized',
      isBeginning: 'is-beginning',
      isEnd: 'is-end',
    };
  }

  get swiperInstance() {
    return this.#swiperInstance;
  }

  get slides() {
    return [...this.querySelectorAll(this.selectors.slide)];
  }

  get options() {
    const scrollbarSelector = this.getAttribute('scrollbar');
    const scrollbarEl = scrollbarSelector ? this.querySelector(scrollbarSelector) : null;

    const prevSelector = this.getAttribute('prev-button');
    const nextSelector = this.getAttribute('next-button');
    const prevEl = prevSelector && (this.querySelector(prevSelector) || document.querySelector(prevSelector));
    const nextEl = nextSelector && (this.querySelector(nextSelector) || document.querySelector(nextSelector));

    const customOptions = {
      ...(scrollbarEl && {
        scrollbar: { el: scrollbarEl, draggable: false },
      }),
      ...(prevEl && nextEl && {
        navigation: { prevEl, nextEl },
      }),
      ...(this.getAttribute('freemode') === 'true' && {
        freeMode: true,
      }),
      ...(this.getAttribute('fade') === 'true' && {
        effect: 'fade',
      }),
      ...(this.getAttribute('center-insufficient-slides') === 'true' && {
        centerInsufficientSlides: true,
      }),
      ...(this.hasAttribute('initial-slide') && {
        initialSlide: parseInt(this.getAttribute('initial-slide'), 10),
      }),
      ...(this.getAttribute('vertical') === 'true' && {
        direction: 'vertical',
        slidesPerView: 1,
        speed: 400,
        resistanceRatio: 0,
      }),
    };

    return {
      ...this.#defaultOptions,
      ...customOptions,
      on: {
        ...this.#defaultOptions.on,
        ...customOptions.on,
      },
    };
  }

  initSlider() {
    if (!window.Swiper) {
      console.warn('Swiper is not available.');
      return;
    }

    if (this.#swiperInstance) return;

    this.#swiperInstance = new window.Swiper(this, this.options);
    this.dispatchEvent(new CustomEvent('slider:initialized', { bubbles: true }));
  }

  destroySlider(deleteInstance = true, clearStyles = false) {
    if (!this.#swiperInstance) return;

    this.#swiperInstance.destroy(deleteInstance, clearStyles);
    this.#swiperInstance = null;
  }

  updateSlider() {
    if (!this.#swiperInstance) return;
    this.#swiperInstance.update();
  }

  goToSlide(index = null) {
    if (index == null || !this.#swiperInstance) return;
    this.#swiperInstance.slideTo(index, 0);
  }

  #onSlideChange() {
    this.#updateSliderStateModifiers();
  }

  #updateSliderStateModifiers() {
    if (!this.#swiperInstance) return;

    this.classList.toggle(this.modifiers.isBeginning, this.#swiperInstance.isBeginning);
    this.classList.toggle(this.modifiers.isEnd, this.#swiperInstance.isEnd);
  }

  #stopPropagationForScrollableChildren() {
    const scrollableChildren = [...this.querySelectorAll(this.selectors.scrollableChildren)];
    if (!scrollableChildren.length) return;

    this.#scrollController = new AbortController();

    scrollableChildren.forEach((child) => {
      child.addEventListener(
        'touchmove',
        (event) => event.stopPropagation(),
        { signal: this.#scrollController.signal }
      );
    });
  }
}

if (!customElements.get('base-slider')) {
  customElements.define('base-slider', BaseSlider);
}

/**
 * @class ScrollSlider
 * @extends BaseSlider
 *
 * Scrollable variant of the Swiper-based custom slider element.
 */
export class ScrollSlider extends BaseSlider {
  #mediaQuery = null;
  #onBreakpointChange = () => this.initSlider();

  connectedCallback() {
    super.connectedCallback();

    this.#mediaQuery = window.matchMedia(`(min-width: ${this.#breakpoints.desktop}px)`);
    this.#mediaQuery.addEventListener('change', this.#onBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#mediaQuery?.removeEventListener('change', this.#onBreakpointChange);
    this.#mediaQuery = null;
  }

  get modifiers() {
    return {
      ...super.modifiers,
      bleed: "slider--bleed",
      scroll: "slider--scroll",
    };
  }

  get #breakpoints() {
    return {
      desktop: 992,
      mobile: 375,
    };
  }

  get #isDesktop() {
    return this.#mediaQuery?.matches ?? window.matchMedia(`(min-width: ${this.#breakpoints.desktop}px)`).matches;
  }

  initSlider() {
    if (this.#isDesktop) {
      this.classList.add(this.modifiers.bleed);
      this.classList.remove(this.modifiers.scroll);

      if (!this.swiperInstance) {
        super.initSlider();
      }
    } else {
      this.classList.remove(this.modifiers.bleed);
      this.classList.add(this.modifiers.scroll);

      if (this.swiperInstance) {
        this.destroySlider();
      }
    }
  }
}

if (!customElements.get('scroll-slider')) {
  customElements.define('scroll-slider', ScrollSlider);
}
